import { useEffect, useState } from 'react';
import { RateImport } from '@/components/logistics/RateImport';
import { Link } from 'react-router-dom';
import { Mode2PricingEditor, M2_DEFAULT, m2Payload } from '@/components/logistics/Mode2PricingEditor';
import { MipaqueteQuoteTest } from '@/components/logistics/MipaqueteQuoteTest';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { ShippingSimulator } from '@/components/ShippingSimulator';

const MODES = [
  { key: 'seller', label: 'Envío gestionado por el vendedor', note: 'Siempre activo' },
  { key: 'automarket', label: 'Envío gestionado por AutoMarket', note: 'Los vendedores que se adhieran: AutoMarket cotiza, contrata y coordina el despacho' },
  { key: 'free_subsidized', label: 'Envío gratis o subsidiado', note: 'Permite aplicar promociones reales en el pago (actívalas en Promociones)' },
];
const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-CO')}`);

const Card = ({ title, children, testid }) => (
  <section className="mt-6 border border-slate-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);

const RowsEditor = ({ rows, setRows, fields, empty, testid }) => (
  <div data-testid={testid}>
    <div className="overflow-x-auto">
    <div className="mb-1 flex items-end gap-2" data-testid={`${testid}-headers`}>
      {fields.map(f => (
        <span key={f.key} className={`${f.w || 'w-32'} shrink-0 text-xs font-semibold leading-tight text-slate-700`} data-testid={`${testid}-header-${f.key}`}>{f.header || f.label}</span>
      ))}
    </div>
    {rows.map((r, i) => (
      <div key={i} className="mb-2 flex items-center gap-2 [&>input]:shrink-0">
        {fields.map(f => (
          <Input key={f.key} type={f.type || 'text'} placeholder={f.label} value={r[f.key] ?? ''}
            className={`rounded-none h-8 text-sm ${f.w || 'w-32'}`}
            onChange={e => setRows(rows.map((x, j) => j === i ? { ...x, [f.key]: f.type === 'number' ? e.target.value : e.target.value } : x))}
            data-testid={`${testid}-${f.key}-${i}`} />
        ))}
        <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ))}
    </div>
    <Button size="sm" variant="outline" className="rounded-none" onClick={() => setRows([...rows, { ...empty }])} data-testid={`${testid}-add`}>
      <Plus className="mr-1 h-4 w-4" /> Agregar
    </Button>
  </div>
);

const LedgerTable = ({ items }) => (
  <div className="overflow-x-auto" data-testid="logistics-ledger-table">
    <table className="w-full text-xs">
      <thead className="text-left text-slate-500"><tr>
        <th className="py-1 pr-3">Pedido</th><th className="pr-3">Cobro comprador</th><th className="pr-3">Abono vendedor</th>
        <th className="pr-3">Costo transportador</th><th className="pr-3">Subsidios</th><th className="pr-3">Tarifa logística</th><th>Fecha</th>
      </tr></thead>
      <tbody>
        {items.map(x => (
          <tr key={x.order_id} className="border-t border-slate-100" data-testid={`logistics-ledger-row-${x.order_id}`}>
            <td className="py-1 pr-3 font-mono"><Link to={`/admin/pedidos/${x.order_id}`} className="text-orange-700 hover:underline">{x.order_id}</Link></td>
            <td className="pr-3">{fmt(x.buyer_charge)}</td><td className="pr-3">{fmt(x.seller_shipping_credit)}</td>
            <td className="pr-3">{fmt(x.carrier_cost)}</td><td className="pr-3">{fmt((x.subsidy_automarket || 0) + (x.subsidy_seller || 0))}</td>
            <td className="pr-3">{fmt(x.logistics_fee)}</td><td>{(x.created_at || '').slice(0, 10)}</td>
          </tr>
        ))}
        {!items.length && <tr><td colSpan={7} className="py-3 text-slate-400">Aún no hay pedidos nuevos registrados.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function AdminLogistics() {
  const [s, setS] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ live_allowed: false });

  useEffect(() => {
    api.get('/admin/logistics/settings').then(r => { setS({ ...r.data.settings, mode2_pricing: { ...M2_DEFAULT, ...(r.data.settings.mode2_pricing || {}) } }); setMeta(r.data); }).catch(() => toast.error('No se pudo cargar la configuración'));
    api.get('/admin/logistics/ledger').then(r => setLedger(r.data.items || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === '' || v == null ? 0 : Number(v));
      await api.put('/admin/logistics/settings', {
        modes: s.modes,
        logistics_fee: { type: s.logistics_fee.type, value: num(s.logistics_fee.value) },
        mode2_pricing: m2Payload(s.mode2_pricing),
        standard_rates: s.standard_rates.map(r => ({ ...r, rate: num(r.rate), days_min: num(r.days_min), days_max: num(r.days_max) })),
        carriers: s.carriers.map(c => ({ name: c.name, active: false, notes: c.notes || null })),
      });
      toast.success('Configuración logística guardada');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (Array.isArray(d) ? 'Revisa los valores ingresados' : d) || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  if (!s) return <Layout><div className="p-10 text-sm text-slate-500">Cargando…</div></Layout>;
  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8 pb-24" data-testid="admin-logistics-page">
        <Link to="/admin/configuracion" className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Configuración</Link>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Envíos y logística</h1>
        <p className="mt-2 text-sm text-slate-600" data-testid="admin-logistics-stage-note">
          Las tarifas estándar se cobran en productos con «Tarifa estándar de AutoMarket» y en todos los pedidos de vendedores con envío gestionado por AutoMarket. En esa modalidad el comprador paga el flete y el vendedor asume el cargo logístico. La comisión no cambia.
        </p>
        {!meta.live_allowed && (
          <p className="mt-3 flex items-center gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="admin-logistics-prod-lock">
            <Lock className="h-4 w-4" /> En producción las modalidades 2 y 3 siguen bloqueadas hasta la autorización expresa.
          </p>
        )}

        <Link to="/admin/configuracion/envios/promociones" className="mt-4 inline-block border border-orange-300 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100" data-testid="admin-logistics-promos-link">
          Promociones y subsidios de envío →
        </Link>
        <Link to="/admin/configuracion/envios/envio-gratis" className="ml-2 mt-4 inline-block border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="admin-logistics-fsr-link">
          Envío gratis desde (reserva) →
        </Link>
        <Link to="/admin/configuracion/envios/despachos" className="ml-2 mt-4 inline-block border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="admin-logistics-shipments-link">
          Recogidas y despachos →
        </Link>
        <Link to="/admin/configuracion/envios/reembolsos" className="ml-2 mt-4 inline-block border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="admin-logistics-refunds-link">
          Devoluciones y reembolsos de envío →
        </Link>

        <Card title="Modalidades" testid="admin-logistics-modes">
          {MODES.map(m => (
            <label key={m.key} className="flex items-center gap-3 py-1.5 text-sm">
              <input type="checkbox" checked={!!s.modes[m.key]} disabled={m.key === 'seller' || !meta.live_allowed}
                onChange={e => setS({ ...s, modes: { ...s.modes, [m.key]: e.target.checked } })} data-testid={`admin-logistics-mode-${m.key}`} />
              <span className="font-medium text-slate-800">{m.label}</span>
              <span className="text-xs text-slate-500 flex items-center gap-1">{m.key !== 'seller' && !meta.live_allowed && <Lock className="h-3 w-3" />}{m.note}</span>
            </label>
          ))}
        </Card>

        <Card title="Cargo logístico al vendedor (envío gestionado por AutoMarket)" testid="admin-logistics-fee">
          <p className="mb-3 text-xs text-slate-500" data-testid="admin-logistics-fee-help">Valor que se descuenta del desembolso del vendedor en cada pedido con envío gestionado por AutoMarket (valor fijo o % del subtotal de productos). {s.modes.automarket ? 'Se aplica a los pedidos nuevos.' : 'Actualmente no se aplica.'}</p>
          <div className="flex flex-wrap items-center gap-2">
            <select className="h-9 border border-slate-300 px-2 text-sm" value={s.logistics_fee.type}
              onChange={e => setS({ ...s, logistics_fee: { ...s.logistics_fee, type: e.target.value } })} data-testid="admin-logistics-fee-type">
              <option value="fixed">Valor fijo (COP)</option><option value="percent">Porcentaje (%)</option>
            </select>
            <Input type="number" min="0" className="rounded-none w-40" value={s.logistics_fee.value}
              onChange={e => setS({ ...s, logistics_fee: { ...s.logistics_fee, value: e.target.value } })} data-testid="admin-logistics-fee-value" />
          </div>
        </Card>

        <Card title="Tarifas estándar de AutoMarket (por departamento y ciudad)" testid="admin-logistics-rates">
          <RateImport onApplied={() => api.get('/admin/logistics/settings').then(r => setS(r.data.settings))} />
          <p className="mb-3 text-xs text-slate-500">Deja la ciudad vacía para la tarifa general del departamento. Si existe una tarifa para la ciudad, tiene prioridad. Departamentos sin fila quedan sin cobertura para productos con «Tarifa estándar de AutoMarket».</p>
          <RowsEditor rows={s.standard_rates} setRows={rows => setS({ ...s, standard_rates: rows })} testid="admin-logistics-rate"
            empty={{ department: '', city: '', rate: '', days_min: 1, days_max: 3 }}
            fields={[{ key: 'department', label: 'Departamento', header: 'Departamento de destino', w: 'w-44' }, { key: 'city', label: 'Ciudad (opcional)', header: 'Ciudad de destino (opcional)', w: 'w-40' }, { key: 'rate', label: 'Valor (COP)', header: 'Valor del envío (COP)', type: 'number' },
              { key: 'days_min', label: 'Días mín', header: 'Días mínimos de entrega', type: 'number', w: 'w-24' }, { key: 'days_max', label: 'Días máx', header: 'Días máximos de entrega', type: 'number', w: 'w-24' }]} />
        </Card>

        <Card title="Motor de tarifas · envío gestionado por AutoMarket" testid="admin-logistics-mode2-pricing">
          <Mode2PricingEditor value={s.mode2_pricing} onChange={v => setS({ ...s, mode2_pricing: v })} />
        </Card>

        <Card title="Prueba de cotización MiPaquete (solo administradores)" testid="admin-logistics-mipaquete">
          <MipaqueteQuoteTest />
        </Card>

        <Card title="Transportadoras habituales (referencia)" testid="admin-logistics-carriers">
          <RowsEditor rows={s.carriers} setRows={rows => setS({ ...s, carriers: rows })} testid="admin-logistics-carrier"
            empty={{ name: '', notes: '' }}
            fields={[{ key: 'name', label: 'Nombre', header: 'Nombre de la transportadora', w: 'w-48' }, { key: 'notes', label: 'Notas', header: 'Notas / Observaciones', w: 'w-72' }]} />
        </Card>

        <div className="mt-6">
          <Button className="rounded-none bg-slate-900" onClick={save} disabled={saving} data-testid="admin-logistics-save">
            {saving ? 'Guardando…' : 'Guardar configuración'}
          </Button>
        </div>

        <Card title="Simulador de envío" testid="admin-logistics-simulator">
          <ShippingSimulator />
        </Card>

        <Card title="Registro de envíos (informativo)" testid="admin-logistics-ledger">
          <LedgerTable items={ledger} />
        </Card>
      </div>
    </Layout>
  );
}
